import React, { useEffect, useMemo, useState } from 'react';
import { resolveShare } from '../lib/shares';
import { buildICS, googleCalendarLink } from '../utils/ics';
import { generateVCard } from '../utils/contactShareUtils';
import QRCode from 'react-qr-code';
import RoundedQRCode from '../components/RoundedQRCode';

type Mode = 'none' | 'contact' | 'event' | 'expired' | 'error';

export default function ShareRouter() {
    const [mode, setMode] = useState<Mode>('none');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);

    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const token = useMemo(() => {
        // Expect /connect/v/:token or /event/v/:token
        const parts = path.split('/').filter(Boolean);
        const t = parts[parts.length - 1];
        return t && t.length >= 8 ? t : '';
    }, [path]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                if (!token) throw new Error('No token');
                const res = await resolveShare(token);
                if (!alive) return;
                if (!res) { setMode('expired'); setData(null); return; }
                setData(res);
                setMode(res.type === 'contact' ? 'contact' : 'event');
            } catch (e) {
                if (!alive) return;
                setMode('error');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [token]);

    if (loading) return splash('Loading…');
    if (mode === 'expired') return splash('This link has expired.');
    if (mode === 'error') return splash('Unable to load this share.');
    if (mode === 'contact') return <ContactPublicPage data={data} />;
    if (mode === 'event') return <EventPublicPage data={data} />;
    return splash('Invalid link');
}

function splash(text: string) {
    return (
        <div className="min-h-screen bg-black text-gray-200 flex items-center justify-center">
            <div className="text-center">
                <img
                    src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                    alt="Vault Contact"
                    className="h-12 w-12 mx-auto mb-4 object-contain"
                />
                <div className="text-sm">{text}</div>
            </div>
        </div>
    );
}

function ContactPublicPage({ data }: { data: any }) {
    const c = (data.contact || {}) as any;
    const vcard = generateVCard({
        first_name: c.first_name,
        last_name: c.last_name,
        email: c.email,
        phone: c.phone || c.mobile,
        mobile: c.mobile,
        company: c.company,
        title: c.title,
        linkedin_profile_url: c.linkedin_profile_url || c.linkedin_url,
    });

    const downloadVcf = () => {
        const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const name = `${(c.first_name || '').trim()}_${(c.last_name || '').trim()}`.replace(/\s+/g, '_') || 'contact';
        a.download = `${name}.vcf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 3000);
    };

    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Contact';

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-md mx-auto p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="text-lg font-semibold">Contact</div>
                    <img src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp" alt="Logo" className="h-6 w-6" />
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <div className="text-2xl font-bold mb-1">{name}</div>
                    <div className="text-gray-300 text-sm mb-3">{[c.title, c.company].filter(Boolean).join(' at ')}</div>
                    <div className="space-y-2 text-sm text-gray-300">
                        {c.email && <div>📧 {c.email}</div>}
                        {(c.phone || c.mobile) && <div>📞 {c.phone || c.mobile}</div>}
                        {c.linkedin_profile_url && <div>🔗 {c.linkedin_profile_url}</div>}
                    </div>
                    <div className="mt-4">
                        <button onClick={downloadVcf} className="w-full px-3 py-2 rounded bg-blue-600 border border-blue-500 text-white text-sm">Download vCard</button>
                    </div>
                    <div className="mt-4 w-full flex justify-center">
                        <div className="p-2 rounded bg-black/40 border border-gray-800">
                            <RoundedQRCode value={window.location.href} size={120} background="#111827" foreground="#e5e7eb" />
                        </div>
                    </div>
                </div>
                <div className="text-center text-xs text-gray-500 mt-6">Powered by Griswold Vault</div>
            </div>
        </div>
    );
}

function EventPublicPage({ data }: { data: any }) {
    const e = (data.event || data.payload || {}) as any;
    const title = e.name || e.title || 'Event';
    const description = e.notes || e.description || '';
    const location = e.where || e.location || '';
    const whenISO = e.when || e.when_date || e.due_date || new Date().toISOString();
    const allDay = !!e.all_day || whenISO.endsWith('T00:00:00.000Z');

    const ics = buildICS({ title, description, location, startISO: whenISO, allDay });

    const downloadIcs = () => {
        const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safe = String(title).trim().replace(/\s+/g, '_') || 'event';
        a.download = `${safe}.ics`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 3000);
    };

    const googleUrl = googleCalendarLink({ title, description, location, startISO: whenISO, allDay });

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-md mx-auto p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="text-lg font-semibold">Event</div>
                    <img src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp" alt="Logo" className="h-6 w-6" />
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <div className="text-2xl font-bold mb-1">{title}</div>
                    <div className="text-gray-300 text-sm mb-3">{new Date(whenISO).toLocaleString()}</div>
                    <div className="space-y-2 text-sm text-gray-300">
                        {location && <div>📍 {location}</div>}
                        {description && <div className="whitespace-pre-wrap">📝 {description}</div>}
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-2">
                        <a href={googleUrl} target="_blank" rel="noreferrer" className="w-full px-3 py-2 text-center rounded bg-green-600 border border-green-500 text-white text-sm">Add to Google Calendar</a>
                        <button onClick={downloadIcs} className="w-full px-3 py-2 rounded bg-blue-600 border border-blue-500 text-white text-sm">Download .ics</button>
                    </div>
                    <div className="mt-4 w-full flex justify-center">
                        <div className="p-2 rounded bg-black/40 border border-gray-800">
                            <RoundedQRCode value={window.location.href} size={120} background="#111827" foreground="#e5e7eb" />
                        </div>
                    </div>
                </div>
                <div className="text-center text-xs text-gray-500 mt-6">Powered by Griswold Vault</div>
            </div>
        </div>
    );
}
